import { CognitoUserPoolConfig } from '@aws-amplify/core'
import { signInWithRedirect, signOut } from 'aws-amplify/auth'
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { AuthConfigResponse, useAuthenticateEmail } from 'src/api/mutation/auth'
import awsConfig from 'src/aws-exports.json'
import { Environments } from 'src/configurations'
import { AuthTypes } from 'src/enums'
import {
  extendSignIn,
  POST_AUTH_FLOW_TYPE_SUCCESS,
} from 'src/redux/actions/auth.actions'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectEnvironmentFromState } from 'src/redux/selectors/session.selectors'
import {
  clearAmplifyState,
  configureAmplify,
  configureAmplifyWithTokenProvider,
  getDefaultCognitoConfig,
} from 'src/utils/cognito.utils'
import { isSSOLoginEnabled } from 'src/utils/featureFlags'
import Cookies from 'universal-cookie'

export function getUserPoolClientIdCookieName(env: Environments) {
  return `extend_aws_amplify_client_id_${env}`
}

export function getUserPoolIdCookieName(env: Environments) {
  return `extend_aws_amplify_user_pool_id_${env}`
}

export type UserContextActions = {
  /** Logs a user in */
  loginUserWithPassword: (
    email: string,
    password?: string,
    newCode?: boolean,
  ) => void
  /**
   * Check user email if exists in SSO params
   * Automatically reconfigures Amplify to suit our login settings
   * per /authConfig API call
   */
  getAuthConfigFromEmail: (email: string) => Promise<AuthConfigResponse>
  /** */
  flushUser: () => void
}

export type UserContextAuthenticatedState = {
  authenticated: true
  // NOTE:
  // I am unsure whether locale should be stored in
  // ConfigProvider or UserProvider
  // I don't know enough about the application to make
  // an informed decision right now, so I'm going to
  // keep this here for now and come to a decision later
  locale: string
  loading: boolean
  user: unknown
}

export type UserContextUnAuthenticatedState = {
  authenticated: false
  authConfig?: {
    authType: 'password' | 'sso'
    userPoolId: string | null
    identityProviderName: string | null
  }
  loading: boolean
  locale: string
}

type UserContextState =
  | UserContextAuthenticatedState
  | UserContextUnAuthenticatedState

export type UserContextValue = UserContextState & UserContextActions

const stubUserContextActions: UserContextActions = {
  loginUserWithPassword: () => {
    throw Error('UserContext Not Available')
  },
  getAuthConfigFromEmail: () => {
    throw Error('UserContext Not Available')
  },
  flushUser: () => {
    throw Error('UserContext Not Available')
  },
}

const defaultUserContextState: UserContextState = {
  authenticated: false,
  locale: navigator?.language || 'en-US',
  loading: false,
}

export const UserContext = createContext<UserContextValue>({
  ...defaultUserContextState,
  ...stubUserContextActions,
})

type UserProviderProps = {
  children: ReactNode
}

export default function UserProvider(props: UserProviderProps): ReactElement {
  const { children } = props
  const { analytics } = useAnalyticsContext()
  const [userState, setUserState] = useState<UserContextState>(
    defaultUserContextState,
  )

  const { mutateAsync: getAuthConfig } = useAuthenticateEmail()
  const dispatch = useDispatch()
  const env = useSelector(selectEnvironmentFromState)
  const featureFlagger = useSelector(selectFeatureFlaggerFromState)
  const ssoEnabled = isSSOLoginEnabled(featureFlagger)

  // Effect to configure Amplify on initialize
  const [didConfigAmplify, setDidConfigAmplify] = useState<boolean>(false)
  useEffect(() => {
    if (didConfigAmplify === false && env) {
      // Use unified configureAmplify for initial setup (no email yet, so no /authconfig call)
      configureAmplify(env)
      setDidConfigAmplify(true)
    }
  }, [env, didConfigAmplify])

  /** Function for populating state with user information */
  const loginUserWithPassword = (
    email: string,
    password?: string,
    newCode?: boolean,
  ): void => {
    dispatch(extendSignIn(email, password, newCode, analytics))
  }

  const getAuthConfigFromEmail = async (
    email: string,
  ): Promise<AuthConfigResponse> => {
    setUserState({
      ...userState,
      loading: true,
    })

    // If SSO is not enabled, use legacy flow without /authconfig call
    if (!ssoEnabled) {
      // Use unified configureAmplify for default setup
      await configureAmplify(env)

      // Return a default auth config for password flow
      const defaultAuthConfig: AuthConfigResponse = {
        authType: 'password',
        clientId: null,
        userPoolId: null,
        identityProviderName: null,
      }

      dispatch({
        type: POST_AUTH_FLOW_TYPE_SUCCESS,
        payload: { type: AuthTypes.cognito },
      })

      setUserState({
        ...userState,
        authenticated: false,
        loading: false,
        authConfig: { ...defaultAuthConfig },
      })

      return defaultAuthConfig
    }

    // SSO is enabled - call /authconfig to determine auth flow
    // Clear existing cookies and localStorage to prevent stale config
    clearAmplifyState(env)

    const cookies = new Cookies()

    try {
      const authConfig = await getAuthConfig(email)
      let localCognitoConfig: CognitoUserPoolConfig

      // Use pool info from API if provided, otherwise use default
      if (authConfig.userPoolId && authConfig.clientId) {
        localCognitoConfig = {
          userPoolId: authConfig.userPoolId,
          userPoolClientId: authConfig.clientId,
        }
        cookies.set(
          getUserPoolClientIdCookieName(env),
          localCognitoConfig.userPoolClientId,
          { path: '/' },
        )
        cookies.set(
          getUserPoolIdCookieName(env),
          localCognitoConfig.userPoolId,
          {
            path: '/',
          },
        )
      } else {
        localCognitoConfig = getDefaultCognitoConfig(env)
      }

      // If this exists, then we need to use SSO for a login and not password
      if (authConfig.identityProviderName) {
        localCognitoConfig.loginWith = {
          oauth: {
            ...(env === Environments.Prod
              ? awsConfig['prod-oauth']
              : awsConfig['stage-oauth']),
            providers: [{ custom: authConfig.identityProviderName }],
            responseType: 'code',
          },
        }
      }
      // Configure Amplify with the correct pool settings
      configureAmplifyWithTokenProvider(localCognitoConfig)

      if (authConfig.authType === 'sso' && authConfig.identityProviderName) {
        // Clear any existing Amplify session before SSO redirect
        // This prevents UserAlreadyAuthenticatedException when Amplify state
        // is out of sync with React app state (e.g., after failed logout)
        try {
          await signOut()
        } catch {
          // Ignore errors - user may not be signed in
        }
        signInWithRedirect({
          provider: {
            custom: authConfig.identityProviderName,
          },
        })
        return authConfig
      }

      // Dispatch authType to Redux so extendSignIn can use it
      // Both SSO and password auth use Cognito in the new flow
      dispatch({
        type: POST_AUTH_FLOW_TYPE_SUCCESS,
        payload: { type: AuthTypes.cognito },
      })

      setUserState({
        ...userState,
        authenticated: false,
        loading: false,
        authConfig: { ...authConfig },
      })

      return authConfig
    } catch (e) {
      // TODO: sso - analytics? logs? smoother error handling?
      return Promise.reject(e)
    }
  }

  const flushUser = (): void => {
    setUserState({
      authenticated: false,
      locale: navigator?.language || 'en-US',
      loading: false,
    })
  }

  return (
    <UserContext.Provider
      value={{
        ...userState,
        loginUserWithPassword,
        flushUser,
        getAuthConfigFromEmail,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
